import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react'
import { create } from '@bufbuild/protobuf'
import { useStore } from '@tanstack/react-store'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Send } from 'lucide-react'
import { toast } from 'sonner'

import { CreateNoteRequestSchema } from '@/gen/cortado/v1/cortado_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { cn } from '@/lib/utils'
import { useCreateNote } from '@/stores/notes.store'
import { organizationStore } from '@/stores/organization.store'
import { useInvalidateJobActivity } from '@/stores/pipeline.store'
import { Button } from '@/components/ui/button'
import { AnimatedCollapse } from '@/components/shared/animated-collapse'
import { NOTE_BODY_CLASS } from '@/components/notes/note-body'
import {
  isNoteMentionSuggestionOpen,
  noteMentionedUserIds,
  useNoteMentionExtension,
} from '@/components/notes/note-mentions'
import { jobNoteTextToHtml } from '@/components/pipeline/job-detail-view'

const NOTE_DRAFT_KEY_PREFIX = 'cortado_job_note_draft_'

/**
 * The unsent note for a job (HTML), kept in sessionStorage so it survives
 * switching Activity tabs or moving between the job and its quote pages
 * mid-call. Drafts saved before the composer took rich text are plain text;
 * they load as one paragraph per line. Storage can be unavailable (private
 * mode, blocked site data); the draft then lives only as long as the composer.
 */
function readNoteDraft(jobId: string): string {
  let raw: string
  try {
    raw = sessionStorage.getItem(NOTE_DRAFT_KEY_PREFIX + jobId) ?? ''
  } catch {
    return ''
  }
  if (!raw || raw.startsWith('<')) return raw
  return jobNoteTextToHtml(raw)
}

function writeNoteDraft(jobId: string, html: string) {
  try {
    if (html) sessionStorage.setItem(NOTE_DRAFT_KEY_PREFIX + jobId, html)
    else sessionStorage.removeItem(NOTE_DRAFT_KEY_PREFIX + jobId)
  } catch {
    // Storage unavailable; the in-memory draft still works.
  }
}

/** Until resized, the box grows with the note from about two lines up to 16rem, then scrolls. */
const AUTO_MAX_HEIGHT = 256
const MIN_HEIGHT = 56
/** The tallest a dragged box gets, as a share of the viewport. */
const MAX_VIEWPORT_SHARE = 0.8
/** How far one arrow-key press on the resize grip moves the box's edge. */
const KEYBOARD_RESIZE_STEP = 24

function clampBoxHeight(height: number): number {
  const max = Math.max(MIN_HEIGHT, Math.round(window.innerHeight * MAX_VIEWPORT_SHARE))
  return Math.min(max, Math.max(MIN_HEIGHT, Math.round(height)))
}

/** shadcn Textarea's look, moved to the wrapper so the ring follows focus inside it. */
const BOX_CLASS =
  'relative w-full rounded-md border border-input bg-transparent shadow-xs transition focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 dark:bg-input/30'

/** TipTap's Placeholder marks the empty first paragraph; draw its text muted, as the note sheet does. */
const PLACEHOLDER_CLASS =
  '[&_p.is-editor-empty:first-child]:before:pointer-events-none [&_p.is-editor-empty:first-child]:before:float-left [&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:text-muted-foreground [&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]'

/**
 * The job's note box: rich text without a toolbar. Markdown-style typing
 * (`- `, `1. `, `**bold**`, `*italic*`, `~~strike~~`, `` `code` ``) and the
 * usual shortcuts format the note, `@` mentions a teammate, Enter starts a new
 * paragraph or list item and ⌘/Ctrl+Enter saves. The Save note row slides in
 * once the box has text. Dragging the bottom-right grip sets the box's height;
 * double-clicking it goes back to growing with the note.
 */
export function JobNoteComposer({ jobId }: { jobId: string }) {
  const selectedOrganizationId = useStore(organizationStore, (state) => state.selectedOrganizationId)
  const { mutation: createNoteMutation } = useCreateNote()
  const invalidateJobActivity = useInvalidateJobActivity()
  const mentionExtension = useNoteMentionExtension()
  // null: grow with the note. A number: the height the user dragged the box to.
  const [boxHeight, setBoxHeight] = useState<number | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<{ startY: number; startHeight: number } | null>(null)
  const [initialContent] = useState(() => readNoteDraft(jobId))
  // Drafts are written for the job the editor currently shows.
  const draftJobIdRef = useRef(jobId)
  const savingRef = useRef(false)
  const isSaving = createNoteMutation.isPending

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        // Block types a saved note does not style; typing `# ` or `> ` stays plain text.
        heading: false,
        codeBlock: false,
        horizontalRule: false,
        blockquote: false,
        // No forced empty paragraph after a closing list: it would be saved
        // as a trailing <p></p>. Enter on an empty list item leaves the list.
        trailingNode: false,
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Placeholder.configure({ placeholder: 'Add a note…' }),
      mentionExtension,
    ],
    [mentionExtension],
  )

  const editor = useEditor({
    extensions,
    content: initialContent,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': 'Add a note',
        class: cn(NOTE_BODY_CLASS, 'px-3 py-2 pr-5 outline-none'),
      },
    },
    onUpdate: ({ editor: current }) => {
      writeNoteDraft(draftJobIdRef.current, current.isEmpty ? '' : current.getHTML())
    },
  })

  // The save row stays out of the way until there is something to save.
  const hasDraft = useEditorState({
    editor,
    selector: ({ editor: current }) => !!current && current.getText().trim() !== '',
  })

  // A route change between jobs keeps the composer mounted; show that job's draft.
  useEffect(() => {
    if (!editor || draftJobIdRef.current === jobId) return
    draftJobIdRef.current = jobId
    editor.commands.setContent(readNoteDraft(jobId), { emitUpdate: false })
  }, [editor, jobId])

  useEffect(() => {
    editor?.setEditable(!isSaving, false)
  }, [editor, isSaving])

  async function addNote() {
    if (!editor || savingRef.current || !selectedOrganizationId) return
    if (editor.getText().trim() === '') return
    const savedJobId = draftJobIdRef.current
    savingRef.current = true
    try {
      await createNoteMutation.mutateAsync(
        create(CreateNoteRequestSchema, {
          organizationId: selectedOrganizationId,
          parentType: ParentTypeEnum.PARENT_TYPE_JOB,
          parentId: savedJobId,
          note: editor.getHTML(),
          mentionedUserIds: noteMentionedUserIds(editor.state.doc),
        }),
      )
      void invalidateJobActivity()
      writeNoteDraft(savedJobId, '')
      // The user may have moved to another job mid-save; leave that job's draft alone.
      if (draftJobIdRef.current === savedJobId) {
        editor.commands.clearContent(false)
      }
    } catch (error) {
      reportError(error, { feature: 'job_note_create', kind: 'mutation' })
      toast.error('Failed to add note', { description: getErrorDescription(error) })
    } finally {
      savingRef.current = false
    }
  }

  /**
   * Runs in the capture phase, ahead of ProseMirror's own key handling, so
   * ⌘/Ctrl+Enter saves instead of inserting a line break. While the @-mention
   * popup is open, Enter is left to it.
   */
  function handleKeyDownCapture(event: KeyboardEvent<HTMLDivElement>) {
    if (editor && isNoteMentionSuggestionOpen(editor.state)) return
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      event.stopPropagation()
      void addNote()
    }
  }

  /** A click on the box's empty space (below short text) puts the caret at the end. */
  function handleBoxMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (event.target !== event.currentTarget || !editor) return
    event.preventDefault()
    editor.commands.focus('end')
  }

  function handleGripPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (!boxRef.current) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    dragRef.current = { startY: event.clientY, startHeight: boxRef.current.getBoundingClientRect().height }
  }

  function handleGripPointerMove(event: PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current
    if (!drag) return
    setBoxHeight(clampBoxHeight(drag.startHeight + event.clientY - drag.startY))
  }

  function handleGripPointerUp(event: PointerEvent<HTMLDivElement>) {
    dragRef.current = null
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  /** Arrow keys move the box's bottom edge; Enter goes back to growing with the note. */
  function handleGripKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      setBoxHeight(null)
      return
    }
    const step = event.key === 'ArrowDown' ? KEYBOARD_RESIZE_STEP : event.key === 'ArrowUp' ? -KEYBOARD_RESIZE_STEP : 0
    if (!step || !boxRef.current) return
    event.preventDefault()
    setBoxHeight(clampBoxHeight((boxHeight ?? boxRef.current.getBoundingClientRect().height) + step))
  }

  return (
    <div className="px-6 pb-3">
      <div
        className={cn(BOX_CLASS, PLACEHOLDER_CLASS, isSaving && 'cursor-not-allowed opacity-50')}
        onKeyDownCapture={handleKeyDownCapture}
      >
        <div
          ref={boxRef}
          data-testid="job-note-box"
          className="min-h-14 overflow-y-auto"
          style={boxHeight === null ? { maxHeight: AUTO_MAX_HEIGHT } : { height: boxHeight }}
          onMouseDown={handleBoxMouseDown}
        >
          <EditorContent editor={editor} />
        </div>
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize note box"
          aria-valuemin={MIN_HEIGHT}
          aria-valuenow={boxHeight ?? undefined}
          tabIndex={0}
          title="Drag to resize · double-click to fit"
          onPointerDown={handleGripPointerDown}
          onPointerMove={handleGripPointerMove}
          onPointerUp={handleGripPointerUp}
          onPointerCancel={handleGripPointerUp}
          onDoubleClick={() => setBoxHeight(null)}
          onKeyDown={handleGripKeyDown}
          className="absolute right-0 bottom-0 flex size-4 cursor-ns-resize touch-none items-end justify-end rounded-br-md p-0.5 text-muted-foreground/60 outline-none hover:text-muted-foreground focus-visible:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50"
        >
          <svg aria-hidden viewBox="0 0 10 10" className="size-2.5" stroke="currentColor" strokeLinecap="round">
            <path d="M9 3 3 9M9 6.5 6.5 9" />
          </svg>
        </div>
      </div>
      <AnimatedCollapse open={hasDraft}>
        <div className="flex items-center justify-between gap-2 pt-1.5">
          <span className="text-xs text-muted-foreground">⌘/Ctrl + Enter to save</span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-label="Save note"
            disabled={isSaving}
            onClick={() => void addNote()}
            className="h-7 text-xs"
          >
            <Send className="h-3.5 w-3.5" />
            Save note
          </Button>
        </div>
      </AnimatedCollapse>
    </div>
  )
}
